import { Injectable, computed, signal } from '@angular/core';

interface LoadingSession {
  id: string; // ID univoco per la sessione
  message: string; // Messaggio di caricamento
  progress: number; // Progresso della sessione (0-1), -1 per indeterminato
}

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  private _loadingSessions = signal<LoadingSession[]>([]); // Lista di sessioni attive

  // Stato globale: almeno una sessione è attiva
  public isLoading = computed(() => this._loadingSessions().length > 0);

  // Messaggio della sessione più recente
  public currentMessage = computed(() => {
    const sessions = this._loadingSessions();
    return sessions.length > 0 ? sessions[sessions.length - 1].message : '';
  });

  // Progresso della sessione più recente
  public currentProgress = computed(() => {
    const sessions = this._loadingSessions();
    return sessions.length > 0 ? sessions[sessions.length - 1].progress : -1;
  });

  /**
   * Avvia una nuova sessione di caricamento.
   */
  public startLoading(id: string, message: string = '', progress: number = -1): void {
    const sessions = this._loadingSessions();
    if (!sessions.find((session) => session.id === id)) {
      this._loadingSessions.set([...sessions, { id, message, progress }]);
    } else {
      this.updateLoading(id, message, progress);
    }
  }

  /**
   * Aggiorna una sessione di caricamento esistente.
   */
  public updateLoading(id: string, message: string, progress: number = -1): void {
    this._loadingSessions.set(
      this._loadingSessions().map((session) =>
        session.id === id ? { ...session, message, progress } : session
      )
    );
  }

  /**
   * Chiude una sessione di caricamento.
   */
  public stopLoading(id: string): void {
    this._loadingSessions.set(
      this._loadingSessions().filter((session) => session.id !== id)
    );
  }

  /**
   * Interrompe tutte le sessioni di caricamento.
   */
  public clearAll(): void {
    this._loadingSessions.set([]);
  }
}
